import type { ZodiacSign } from "@/features/advisors"

import elementFire from "@/assets/img/profile/elements/element_fire_2x.webp"
import elementEarth from "@/assets/img/profile/elements/element_earth_2x.webp"
import elementAir from "@/assets/img/profile/elements/element_air_2x.webp"
import elementWater from "@/assets/img/profile/elements/element_water_2x.webp"
import modalityCardinal from "@/assets/img/profile/modality/modality_cardinal_2x.webp"
import modalityFixed from "@/assets/img/profile/modality/modality_fixed_2x.webp"
import modalityMutable from "@/assets/img/profile/modality/modality_mutable_2x.webp"
import masculine from "@/assets/img/profile/polarity/masculine.webp"
import feminine from "@/assets/img/profile/polarity/feminine.webp"
import planetMars from "@/assets/img/profile/planets/planet_mars_2x.webp"
import planetVenus from "@/assets/img/profile/planets/planet_venus_2x.webp"
import planetMercury from "@/assets/img/profile/planets/planet_mercury_2x.webp"
import planetMoon from "@/assets/img/profile/planets/planet_moon_2x.webp"
import planetSun from "@/assets/img/profile/planets/planet_sun_2x.webp"
import planetPluto from "@/assets/img/profile/planets/planet_pluto_2x.webp"
import planetJupiter from "@/assets/img/profile/planets/planet_jupiter_2x.webp"
import planetSaturn from "@/assets/img/profile/planets/planet_saturn_2x.webp"
import planetUranus from "@/assets/img/profile/planets/planet_uranus_2x.webp"
import planetNeptune from "@/assets/img/profile/planets/planet_neptune_2x.webp"

/** An astrology fact rendered with a webp icon. */
export interface ImageFact {
  image: string
  title: string
}

const ELEMENT: Record<ZodiacSign, ImageFact> = {
  Aries: { image: elementFire, title: "Fire" },
  Leo: { image: elementFire, title: "Fire" },
  Sagittarius: { image: elementFire, title: "Fire" },
  Taurus: { image: elementEarth, title: "Earth" },
  Virgo: { image: elementEarth, title: "Earth" },
  Capricorn: { image: elementEarth, title: "Earth" },
  Gemini: { image: elementAir, title: "Air" },
  Libra: { image: elementAir, title: "Air" },
  Aquarius: { image: elementAir, title: "Air" },
  Cancer: { image: elementWater, title: "Water" },
  Scorpio: { image: elementWater, title: "Water" },
  Pisces: { image: elementWater, title: "Water" },
}

const POLARITY: Record<ZodiacSign, ImageFact> = {
  Aries: { image: masculine, title: "Masculine" },
  Gemini: { image: masculine, title: "Masculine" },
  Leo: { image: masculine, title: "Masculine" },
  Libra: { image: masculine, title: "Masculine" },
  Sagittarius: { image: masculine, title: "Masculine" },
  Aquarius: { image: masculine, title: "Masculine" },
  Taurus: { image: feminine, title: "Feminine" },
  Cancer: { image: feminine, title: "Feminine" },
  Virgo: { image: feminine, title: "Feminine" },
  Scorpio: { image: feminine, title: "Feminine" },
  Capricorn: { image: feminine, title: "Feminine" },
  Pisces: { image: feminine, title: "Feminine" },
}

const MODALITY: Record<ZodiacSign, ImageFact> = {
  Aries: { image: modalityCardinal, title: "Cardinal" },
  Cancer: { image: modalityCardinal, title: "Cardinal" },
  Libra: { image: modalityCardinal, title: "Cardinal" },
  Capricorn: { image: modalityCardinal, title: "Cardinal" },
  Taurus: { image: modalityFixed, title: "Fixed" },
  Leo: { image: modalityFixed, title: "Fixed" },
  Scorpio: { image: modalityFixed, title: "Fixed" },
  Aquarius: { image: modalityFixed, title: "Fixed" },
  Gemini: { image: modalityMutable, title: "Mutable" },
  Virgo: { image: modalityMutable, title: "Mutable" },
  Sagittarius: { image: modalityMutable, title: "Mutable" },
  Pisces: { image: modalityMutable, title: "Mutable" },
}

const RULING_PLANET: Record<ZodiacSign, ImageFact> = {
  Aries: { image: planetMars, title: "Mars" },
  Taurus: { image: planetVenus, title: "Venus" },
  Gemini: { image: planetMercury, title: "Mercury" },
  Cancer: { image: planetMoon, title: "Moon" },
  Leo: { image: planetSun, title: "Sun" },
  Virgo: { image: planetMercury, title: "Mercury" },
  Libra: { image: planetVenus, title: "Venus" },
  Scorpio: { image: planetPluto, title: "Pluto" },
  Sagittarius: { image: planetJupiter, title: "Jupiter" },
  Capricorn: { image: planetSaturn, title: "Saturn" },
  Aquarius: { image: planetUranus, title: "Uranus" },
  Pisces: { image: planetNeptune, title: "Neptune" },
}

/** Opposite sign (best "zodiac match"), rendered with a zodiac icon. */
const MATCH: Record<ZodiacSign, ZodiacSign> = {
  Aries: "Libra",
  Taurus: "Scorpio",
  Gemini: "Sagittarius",
  Cancer: "Capricorn",
  Leo: "Aquarius",
  Virgo: "Pisces",
  Libra: "Aries",
  Scorpio: "Taurus",
  Sagittarius: "Gemini",
  Capricorn: "Cancer",
  Aquarius: "Leo",
  Pisces: "Virgo",
}

const MOON_SIGNS: ZodiacSign[] = [
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
]

export const getElement = (sign: ZodiacSign): ImageFact => ELEMENT[sign]
export const getPolarity = (sign: ZodiacSign): ImageFact => POLARITY[sign]
export const getModality = (sign: ZodiacSign): ImageFact => MODALITY[sign]
export const getRulingPlanet = (sign: ZodiacSign): ImageFact => RULING_PLANET[sign]
export const getMatchSign = (sign: ZodiacSign): ZodiacSign => MATCH[sign]

/** Approximate moon sign from a birth date (low-precision lunar longitude). */
export function getMoonSign(birthDate: Date): ZodiacSign {
  const jd = birthDate.getTime() / 86_400_000 + 2440587.5
  const d = jd - 2451545.0
  const normalize = (angle: number) => ((angle % 360) + 360) % 360
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const meanLongitude = normalize(218.316 + 13.176396 * d)
  const meanAnomaly = normalize(134.963 + 13.064993 * d)
  const longitude = normalize(meanLongitude + 6.289 * Math.sin(toRad(meanAnomaly)))
  return MOON_SIGNS[Math.floor(longitude / 30)]
}
